import { useEffect, useState } from "react";
import "./BillingForm.scss";
import { Form, Typography, Input } from "antd";
import { getCaptcha } from "../../../../features/captcha/captchaActions";
import {
  PhoneNumberInput,
  Captcha,
  AddressAutocomplete,
} from "../../../../components";
import billingFormSchema from "../../../../validations/billingFormValidation";
import { createYupValidator } from "../../../../utils/validationHelper";
import Utils from "../../../../utils";

const { TextArea } = Input;

export default function BillingForm({
  formRef,
  addCartDetails,
  loading,
  error,
  dispatch,
  resetPatientError,
}) {
  const [captcha, setCaptcha] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const reGenerateCaptcha = () => {
    setIsLoading(true);
    dispatch(getCaptcha())?.then((res) => {
      if (res?.type === "get_captcha/fulfilled") {
        setCaptcha(res?.payload);
      }
      setIsLoading(false);
    });
  };

  useEffect(() => {
    setIsLoading(true);
    dispatch(getCaptcha())?.then((res) => {
      if (res?.type === "get_captcha/fulfilled") {
        setCaptcha(res?.payload);
      }
      setIsLoading(false);
    });
  }, [dispatch]);

  return (
    <Form
      className="cartForm-cont"
      ref={formRef}
      layout="vertical"
      onFinish={(values) => {
        addCartDetails(
          {
            ...values,
            captcha_key: captcha?.captcha_key ? captcha?.captcha_key : null,
          },
          reGenerateCaptcha
        );
      }}
      onFinishFailed={(error) => {
        Utils?.scrollIntoViewByClassName(".cartForm-cont");
      }}>
      <div className="cartForm-cont__formItemWrapper">
        <Form.Item
          className="cartForm-cont__formItemWrapper__formItem"
          name="first_name"
          rules={[
            {
              required: true,
              validator: createYupValidator(billingFormSchema, "first_name"),
            },
          ]}
          label={
            <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
              First Name
            </Typography.Text>
          }
          colon={false}>
          <Input
            placeholder="First name"
            size="large"
            disabled={loading}
            onChange={() => {
              if (error && error["first_name"]) {
                dispatch(resetPatientError("first_name"));
              }
            }}
          />
        </Form.Item>
        <Form.Item
          className="cartForm-cont__formItemWrapper__formItem"
          name="last_name"
          rules={[
            {
              required: true,
              validator: createYupValidator(billingFormSchema, "last_name"),
            },
          ]}
          label={
            <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
              Last Name
            </Typography.Text>
          }
          colon={false}>
          <Input
            disabled={loading}
            placeholder="Last name"
            size="large"
            onChange={() => {
              if (error && error["last_name"]) {
                dispatch(resetPatientError("last_name"));
              }
            }}
          />
        </Form.Item>
      </div>
      <div className="cartForm-cont__formItemWrapper">
        <Form.Item
          className="cartForm-cont__formItemWrapper__formItem"
          name="phone_number"
          rules={[
            {
              required: true,
              validator: createYupValidator(billingFormSchema, "phone_number"),
            },
          ]}
          label={
            <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
              Phone
            </Typography.Text>
          }
          colon={false}>
          <PhoneNumberInput
            isDisabled={loading}
            placeholder="Phone"
            size="large"
            onChange={() => {
              if (error && error["phone_number"]) {
                dispatch(resetPatientError("phone_number"));
              }
            }}
          />
        </Form.Item>
        <Form.Item
          className="cartForm-cont__formItemWrapper__formItem"
          name="email"
          rules={[
            {
              required: true,
              validator: createYupValidator(billingFormSchema, "email"),
            },
          ]}
          label={
            <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
              Email
            </Typography.Text>
          }
          colon={false}>
          <Input
            disabled={loading}
            type="text"
            placeholder="Email"
            size="large"
            onChange={() => {
              if (error && error["email"]) {
                dispatch(resetPatientError("email"));
              }
            }}
          />
        </Form.Item>
      </div>
      <div className="cartForm-cont__formItemWrapper">
        <Form.Item
          className="cartForm-cont__formItemWrapper__formItem"
          name="address"
          rules={[
            {
              required: true,
              validator: createYupValidator(billingFormSchema, "address"),
            },
          ]}
          label={
            <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
              Address
            </Typography.Text>
          }
          extra="Start typing to autocomplete City, State, and Zip Code"
          colon={false}>
          <AddressAutocomplete
            placeholder="Address"
            size="large"
            inputProps={{ disabled: loading }}
            onChange={() => {
              // Typed text may no longer match the picked pin, so drop it;
              // a suggestion pick restores it in onAddressSelect.
              formRef?.current?.setFieldsValue({
                latitude: null,
                longitude: null,
              });
              if (error && error["address"]) {
                dispatch(resetPatientError("address"));
              }
            }}
            onAddressSelect={({
              street,
              city,
              state,
              zipcode,
              latitude,
              longitude,
            }) => {
              const fullAddress = [street, city, state, zipcode]
                .filter(Boolean)
                .join(", ");
              formRef?.current?.setFieldsValue({
                address: fullAddress,
                city,
                state,
                zipcode,
                latitude: latitude ?? null,
                longitude: longitude ?? null,
              });
              ["city", "state", "zipcode"]?.forEach((field) => {
                if (error && error[field]) {
                  dispatch(resetPatientError(field));
                }
              });
            }}
          />
        </Form.Item>
        <Form.Item name="latitude" hidden>
          <Input />
        </Form.Item>
        <Form.Item name="longitude" hidden>
          <Input />
        </Form.Item>
      </div>
      <div className="cartForm-cont__formItemWrapper">
        <Form.Item
          className="cartForm-cont__formItemWrapper__formItem"
          name="city"
          rules={[
            {
              required: true,
              validator: createYupValidator(billingFormSchema, "city"),
            },
          ]}
          label={
            <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
              City
            </Typography.Text>
          }
          colon={false}>
          <Input
            disabled={loading}
            placeholder="City"
            size="large"
            onChange={() => {
              if (error && error["city"]) {
                dispatch(resetPatientError("city"));
              }
            }}
          />
        </Form.Item>
        <div className="cartForm-cont__formItemWrapper__cont">
          <Form.Item
            className="cartForm-cont__formItemWrapper__formItem"
            name="state"
            rules={[
              {
                required: true,
                validator: createYupValidator(billingFormSchema, "state"),
              },
            ]}
            label={
              <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
                State
              </Typography.Text>
            }
            colon={false}>
            <Input
              disabled={loading}
              type="text"
              placeholder="State"
              size="large"
              onChange={() => {
                if (error && error["state"]) {
                  dispatch(resetPatientError("state"));
                }
              }}
            />
          </Form.Item>
          <Form.Item
            className="cartForm-cont__formItemWrapper__formItem"
            name="zipcode"
            rules={[
              {
                required: true,
                validator: createYupValidator(billingFormSchema, "zipcode"),
              },
            ]}
            label={
              <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
                Zip Code
              </Typography.Text>
            }
            colon={false}>
            <Input
              disabled={loading}
              placeholder="Zip code"
              size="large"
              onChange={() => {
                if (error && error["zipcode"]) {
                  dispatch(resetPatientError("zipcode"));
                }
              }}
            />
          </Form.Item>
        </div>
      </div>
      <div className="cartForm-cont__formItemWrapper">
        <Form.Item
          className="cartForm-cont__formItemWrapper__formItemLong"
          name="details"
          label={
            <Typography.Text className="cartForm-cont__formItemWrapper__formItem__text">
              Details
            </Typography.Text>
          }
          colon={false}>
          <TextArea
            disabled={loading}
            placeholder="Details"
            autoSize={{ minRows: 3, maxRows: 6 }}
            onChange={() => {
              if (error && error["details"]) {
                dispatch(resetPatientError("details"));
              }
            }}
          />
        </Form.Item>
      </div>
      {captcha ? (
        <div className="cartForm-cont__formItemWrapper">
          <Captcha
            captcha={captcha}
            isLoading={isLoading}
            setCaptcha={setCaptcha}
            setIsLoading={setIsLoading}
          />
          <Form.Item
            className="cartForm-cont__formItemWrapper__formItem"
            name="captcha_value"
            rules={[
              {
                required: true,
                validator: createYupValidator(
                  billingFormSchema,
                  "captcha_value"
                ),
              },
            ]}
            colon={false}>
            <Input
              placeholder="Captcha value"
              size="large"
              disabled={loading}
              onChange={() => {
                if (error && error["captcha_value"]) {
                  dispatch(resetPatientError("captcha_value"));
                }
                if (error && error["non_field_errors"]) {
                  if (error["non_field_errors"][0]?.includes("captcha")) {
                    dispatch(resetPatientError("non_field_errors"));
                  }
                }
              }}
            />
          </Form.Item>
        </div>
      ) : null}
    </Form>
  );
}
